import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Link,
  MenuItem,
  Text,
  useBoolean,
} from "@chakra-ui/react";
import { BatchItem, FILE_STATES } from "@rpldy/uploady";
import { useQueryClient } from "@tanstack/react-query";
import { CapitalEventNoticesTable } from "features/capital-activity/event/CapitalEventNoticesTable";
import { useDocumentStatusUploadCompleteUpdate } from "gen/endpoints/documents/documents";
import {
  getFundFamilyEventRetrieveQueryKey,
  getNoticeDocumentTableDataRetrieveQueryKey,
  useFundFamilyEventRetrieveSuspense,
  useFundFamilyEventUpdate,
  useInvestmentMemoDestroy,
  useInvestmentMemoListSuspense,
  useNoticeDestroy,
  useNoticeDocumentDownloadCreate,
  useNoticeDocumentGenerationStatusRetrieve,
  useNoticesCreate,
  useUpsertInvestmentMemo,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { CeleryWorkflowStatus } from "gen/model/celeryWorkflowStatus";
import { DocumentCreateResponseData } from "gen/model/documentCreateResponseData";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import { FundFamilyEventEditDataRequest } from "gen/model/fundFamilyEventEditDataRequest";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { NoticeDocumentGenerationStatusData } from "gen/model/noticeDocumentGenerationStatusData";
import { NoticeStatusEnum } from "gen/model/noticeStatusEnum";
import { get } from "lodash-es";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Collapsible from "shared/components/collapsible/Collapsible";
import { DocumentList } from "shared/components/document/DocumentList";
import Form from "shared/components/form/Form";
import { FormTextArea } from "shared/components/input/text/TextArea";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useToast } from "shared/components/toast/useToast";
import { FileUpload } from "shared/components/upload/FileUpload";
import { uploadAcceptPdf } from "shared/components/upload/FileUploadUtils";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { downloadFileBlob } from "shared/utils/utils";

interface CapitalEventNoticeGenerationCardProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  isLoading: boolean;
  generateNotices: () => Promise<void>;
  noticeGenerationStatus: NoticeDocumentGenerationStatusData | undefined;
  celeryWorkflowStatus: CeleryWorkflowStatus | undefined;
}

const CapitalEventNoticeGenerationCard: React.FC<
  CapitalEventNoticeGenerationCardProps
> = (props) => {
  const {
    fundFamilyId,
    fundFamilyEventId,
    isLoading,
    generateNotices,
    celeryWorkflowStatus,
  } = props;
  const { t } = useTranslation(["common", "notices"]);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const {
    mutateAsync: upsertInvestmentMemo,
    isPending: isUpsertingInvestmentMemo,
  } = useUpsertInvestmentMemo();

  const { mutateAsync: documentStatusUploadCompleteUpdate } =
    useDocumentStatusUploadCompleteUpdate();

  const { mutateAsync: deleteInvestmentMemo } = useInvestmentMemoDestroy();

  const { data: listData, refetch: refetchInvestmentMemo } =
    useInvestmentMemoListSuspense(fundFamilyId, fundFamilyEventId);

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );
  const refetchDocuments = useCallback(async () => {
    await Promise.all([
      refetchInvestmentMemo(),
      queryClient.invalidateQueries({
        queryKey: getNoticeDocumentTableDataRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
      }),
    ]);
  }, [refetchInvestmentMemo, queryClient, fundFamilyEventId, fundFamilyId]);

  const onItemFinished = useCallback(
    async (item: BatchItem) => {
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      const document: DocumentCreateResponseData = get(item, "document")!;
      await documentStatusUploadCompleteUpdate({ id: document.id });
      await upsertInvestmentMemo({
        fundFamilyId,
        fundFamilyEventId,
        data: {
          document_id: document.id,
          fund_family_event_id: fundFamilyEventId,
        },
      });
      await refetchDocuments();
    },
    [
      upsertInvestmentMemo,
      documentStatusUploadCompleteUpdate,
      fundFamilyId,
      fundFamilyEventId,
      refetchDocuments,
    ],
  );

  const [uploadItems, setUploadItems] = useState<BatchItem[]>([]);

  const areAllUploadsFinished = uploadItems.every(
    (item) => item.state === FILE_STATES.FINISHED,
  );
  const form = useForm<FundFamilyEventEditDataRequest>({
    defaultValues: {
      ...fundFamilyEventData,
      custom_notice_text: fundFamilyEventData.custom_notice_text ?? "",
      notice_generation_celery_workflow_id:
        fundFamilyEventData.notice_generation_celery_workflow_id ?? null,
      capital_call_effective_date:
        fundFamilyEventData.capital_call_effective_date ?? null,
      capital_call_posted_date:
        fundFamilyEventData.capital_call_posted_date ?? null,
      distribution_withholding_upload_document_id:
        fundFamilyEventData.distribution_withholding_upload_document_id ?? null,
      celery_workflow_id: fundFamilyEventData.celery_workflow_id ?? null,
    },
  });
  const { mutateAsync: updateFundFamilyEvent } = useFundFamilyEventUpdate({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getFundFamilyEventRetrieveQueryKey(
            fundFamilyId,
            fundFamilyEventId,
          ),
        });
      },
    },
  });
  const onSubmit = async (data: FundFamilyEventEditDataRequest) => {
    await updateFundFamilyEvent({
      fundFamilyId,
      fundFamilyEventId,
      data: {
        ...fundFamilyEventData,
        ...data,
      },
    });
  };

  const isDisabled = !areAllUploadsFinished || isUpsertingInvestmentMemo;
  const [isOpen, { toggle: toggleOpen }] = useBoolean(false);
  return (
    <Box width="500px">
      <Card>
        <CardBody>
          <Text
            fontSize="16px"
            fontWeight="medium"
            textColor="gray.700"
            marginTop={2}
            data-ls-enabled={true}
            data-public={true}
          >
            {t("notices:generateNotices")}
          </Text>
          <Box mb="24px" marginTop={4}>
            <Text textColor="gray.600" marginBottom={4}>
              {t("notices:configuration.description")}
              <Link
                onClick={() =>
                  navigate(
                    rootRoute({})
                      .fundFamilies({})
                      .fundFamily({ fundFamilyId })
                      .setup({})
                      .notices({}).$,
                  )
                }
              >
                {t("notices:configuration.title")}
              </Link>
              .
            </Text>
            <Text>{t("notices:uploadMemo")}</Text>
            <FileUpload
              documentType={DocumentTypeEnum.investment_memo}
              accept={uploadAcceptPdf}
              onItemsChanged={setUploadItems}
              onItemFinished={onItemFinished}
              itemFilter={() => false}
            />
            <DocumentList
              documents={listData}
              onDocumentDeleted={async () => {
                await deleteInvestmentMemo({
                  fundFamilyId,
                  fundFamilyEventId,
                });
                await refetchDocuments();
              }}
            />
            <Form form={form} onSubmit={onSubmit} onSuccess={generateNotices}>
              <Collapsible
                isOpen={isOpen}
                toggleOpen={toggleOpen}
                label={t("notices:customText.customText")}
                mt="16px"
              >
                <Text mb="10px">{t("notices:customText.info")}</Text>
                <FormTextArea
                  title={t("notices:customText.customText")}
                  name="custom_notice_text"
                />
              </Collapsible>
              <FormSubmitButton
                isLoading={isLoading}
                isDisabled={isDisabled}
                marginTop={4}
                ml="auto"
                display="block"
                colorScheme="secondary"
              >
                {t("notices:generateNotices")}
              </FormSubmitButton>
            </Form>
            {celeryWorkflowStatus?.state ===
              CeleryWorkflowStateMachineFieldsEnum.processing && (
              <Text fontSize="10px" color="gray.500" marginTop={2}>
                {t("notices:create.inProgress")}
              </Text>
            )}
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
};

export const CapitalEventNoticePage: React.FC = () => {
  const { t } = useTranslation(["common", "notices"]);

  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  const queryClient = useQueryClient();

  const { mutateAsync: createNotices, isPending: isLoadingNoticeCreation } =
    useNoticesCreate({
      mutation: {
        onMutate: () =>
          showToast({
            message: t("notices:create.started"),
            variant: "info",
          }),
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getFundFamilyEventQueryKey,
          });
        },
        onError: async (err) => {
          await queryClient.invalidateQueries({
            queryKey: getNoticeDocumentTableDataRetrieveQueryKey(
              fundFamilyId,
              fundFamilyEventId,
            ),
          });
          showErrorToast(err);
        },
      },
    });
  const { mutateAsync: downloadNotices, isPending: isLoadingNoticeDownloads } =
    useNoticeDocumentDownloadCreate();
  const { mutateAsync: deleteNotices } = useNoticeDestroy();

  const { showToast, showErrorToast } = useToast();

  const { data: fundFamilyEventData, queryKey: getFundFamilyEventQueryKey } =
    useFundFamilyEventRetrieveSuspense(fundFamilyId, fundFamilyEventId);

  const isDraftEvent: boolean =
    fundFamilyEventData.state == FundFamilyEventStateMachineFieldsEnum.draft;
  const {
    data: noticeGenerationStatus,
    queryKey: getNoticeDocumentGenerationStatusRetrieveQueryKey,
  } = useNoticeDocumentGenerationStatusRetrieve(
    fundFamilyId,
    fundFamilyEventId,
  );

  const renderToastsForNoticeGeneration = (isError: boolean) => {
    if (isError) {
      showToast({
        message: t("notices:create.error"),
        variant: "error",
      });
    } else {
      showToast({
        message: t("notices:create.success"),
        variant: "success",
      });
    }
  };

  const refetchTableData = async () => {
    await queryClient.invalidateQueries({
      queryKey: getNoticeDocumentTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
    });
    await queryClient.invalidateQueries({
      queryKey: getNoticeDocumentGenerationStatusRetrieveQueryKey,
    });
  };
  const workflowStatuses = useCeleryWorkflowPoll(
    fundFamilyEventData.notice_generation_celery_workflow_id
      ? [fundFamilyEventData.notice_generation_celery_workflow_id]
      : undefined,
    renderToastsForNoticeGeneration,
    refetchTableData,
    refetchTableData,
  );

  const generateNotices = async () => {
    await createNotices({
      fundFamilyId,
      fundFamilyEventId,
    });
  };

  const downloadAll = async () => {
    try {
      const fileDownload = await downloadNotices({
        fundFamilyId,
        fundFamilyEventId,
      });
      const date = new Date().toISOString().slice(0, 10);
      const zipName = t("notices:zipName") + date + ".zip";
      downloadFileBlob(fileDownload, zipName);
      showToast({
        message: t("notices:download.success"),
        variant: "success",
      });
    } catch (err) {
      showErrorToast(err);
    }
  };

  const confirmDeleteNotices = useConfirmationDialog(DeleteConfirmationDialog, {
    title: t("notices:deleteNoticesDialog.title"),
    description: t("notices:deleteNoticesDialog.description"),
    successMessage: t("notices:deleteNoticesDialog.success"),
    performDelete: async () => {
      await deleteNotices({
        fundFamilyId,
        fundFamilyEventId,
      });
      await queryClient.invalidateQueries({
        queryKey: getNoticeDocumentGenerationStatusRetrieveQueryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: getNoticeDocumentTableDataRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
      });
    },
  });

  const isLoading = isLoadingNoticeCreation || isLoadingNoticeDownloads;

  const isOutOfDate =
    noticeGenerationStatus?.status === NoticeStatusEnum.out_of_date;
  const isFailedGeneration =
    noticeGenerationStatus?.status === NoticeStatusEnum.failed_generation;
  const isSuccessfulGeneration =
    noticeGenerationStatus?.status === NoticeStatusEnum.successful_generation;

  return (
    <PageContent contentWidth="boxed">
      <PageHeader
        title={t("common:notices")}
        backButton={{
          title: t("common:capitalEvent"),
          path: "..",
        }}
        actions={
          isSuccessfulGeneration ? (
            <>
              <Button
                size="sm"
                colorScheme="tertiary"
                onClick={downloadAll}
                isLoading={isLoading}
              >
                {t("notices:download.allButton")}
              </Button>
              <PageMenu>
                <MenuItem
                  className="destructive"
                  onClick={confirmDeleteNotices}
                >
                  {t("common:delete")}
                </MenuItem>
              </PageMenu>
            </>
          ) : null
        }
      />
      <PageBody>
        {(isOutOfDate || isFailedGeneration) && (
          <Alert
            data-ls-enabled={true}
            data-public={true}
            status={isFailedGeneration ? "error" : "warning"}
            mt="13px"
            mb="13px"
          >
            <AlertIcon>
              <InfoCircleIcon />
            </AlertIcon>
            <AlertDescription>
              {isFailedGeneration
                ? t("notices:create.alertFailed")
                : t("notices:create.alertOutOfDate")}
            </AlertDescription>
          </Alert>
        )}
        <Box>
          <Flex
            w="full"
            flex={1}
            gap={4}
            _last={{
              "& .chakra-form-control": {
                marginBottom: 0,
              },
            }}
          >
            {isDraftEvent && (
              <CapitalEventNoticeGenerationCard
                fundFamilyId={fundFamilyId}
                fundFamilyEventId={fundFamilyEventId}
                noticeGenerationStatus={noticeGenerationStatus}
                generateNotices={generateNotices}
                isLoading={isLoading}
                celeryWorkflowStatus={workflowStatuses.get(
                  fundFamilyEventData.notice_generation_celery_workflow_id ??
                    "",
                )}
              />
            )}
            <Box flex={1}>
              <CapitalEventNoticesTable
                fundFamilyId={fundFamilyId}
                fundFamilyEventId={fundFamilyEventId}
                isDraftEvent={isDraftEvent}
              />
            </Box>
          </Flex>
        </Box>
      </PageBody>
    </PageContent>
  );
};
